import {useEffect, useState} from '@wordpress/element';
import {useCatalog} from "../../store/catalog";

const getPageWindows = (current, total, delta = 1) => {
    if (total <= 5) return Array.from({length: total}, (_, i) => i);
    const pages = [];
    const left = current - delta, right = current + delta;
    let prev = null;
    for (let i = 0; i < total; i++) {
        if (i === 0 || i === total - 1 || (i >= left && i <= right)) {
            if (prev !== null && i - prev > 1) pages.push('ellipsis');
            pages.push(i);
            prev = i;
        }
    }
    return pages;
};

const BarSkeleton = () => (
    <div className="flex items-center justify-between sm:justify-end gap-2 w-full animate-pulse">
        {/* Mobile skeleton: arrows + count */}
        <div className="h-[30px] w-[30px] bg-base-300 rounded-lg sm:hidden"/>
        <div className="h-3 w-24 bg-base-300 rounded sm:hidden"/>
        <div className="h-[30px] w-[30px] bg-base-300 rounded-lg sm:hidden"/>
        {/* Desktop skeleton: page buttons + jump */}
        <div className="hidden sm:flex items-center gap-1">
            <div className="h-[30px] w-[30px] bg-base-300 rounded-lg"/>
            <div className="h-[30px] w-[30px] bg-base-300 rounded-lg"/>
            <div className="h-[30px] w-[30px] bg-base-300 rounded-lg"/>
            <div className="h-[30px] w-[30px] bg-base-300 rounded-lg"/>
            <div className="h-[30px] w-[30px] bg-base-300 rounded-lg"/>
            <div className="h-[30px] w-10 bg-base-300 rounded-lg ml-2"/>
        </div>
    </div>
);

const FooterSkeleton = () => (
    <div className="flex flex-col items-center gap-3 animate-pulse">
        <div className="flex items-center gap-1">
            <div className="h-[34px] w-[34px] bg-base-300 rounded-lg"/>
            {Array.from({length: 4}).map((_, i) => (
                <div key={i} className="h-[34px] w-[34px] bg-base-300 rounded-lg"/>
            ))}
            <div className="h-[34px] w-[34px] bg-base-300 rounded-lg"/>
        </div>
        <div className="flex items-center gap-2">
            <div className="h-3 w-20 bg-base-300 rounded"/>
            <div className="w-px h-4 bg-base-300"/>
            <div className="h-[30px] w-12 bg-base-300 rounded-lg"/>
            <div className="h-3 w-10 bg-base-300 rounded"/>
        </div>
    </div>
);

export function Pagination({variant = 'footer'}) {
    const {page, setPage, data, loading, size} = useCatalog();
    const [jumpValue, setJumpValue] = useState('');
    const [shouldScroll, setShouldScroll] = useState(false);

    const totalPages = data?.totalPages ?? 0;
    const totalElements = data?.totalElements ?? 0;
    const isBar = variant === 'bar';

    useEffect(() => {
        if (shouldScroll && !loading && data) {
            window.scrollTo({top: 0, behavior: 'instant'});
            setShouldScroll(false);
        }
    }, [loading, data, shouldScroll]);

    const goToPage = (p) => {
        setPage(p);
        if (variant === 'footer') setShouldScroll(true);
    };

    if (loading && !data) {
        return isBar ? <BarSkeleton/> : <FooterSkeleton/>;
    }

    if (!data || totalPages <= 1) return null;

    const isFirst = page === 0;
    const isLast = page >= totalPages - 1;
    const from = totalElements === 0 ? 0 : page * size + 1;
    const to = Math.min((page + 1) * size, totalElements);
    const pages = getPageWindows(page, totalPages, isBar ? 1 : 2);

    const handleJump = (e) => {
        if (e.key === 'Enter' || e.type === 'blur') {
            const p = parseInt(jumpValue, 10);
            if (!isNaN(p) && p >= 1 && p <= totalPages) goToPage(p - 1);
            setJumpValue('');
        }
    };

    const count = (
        <span className="text-[10px] font-black pt-dark-blue uppercase tracking-widest whitespace-nowrap">
            {from}–{to} of {totalElements}
        </span>
    );

    const jumpInput = (
        <div className="flex items-center gap-1 shrink-0 whitespace-nowrap text-sm text-base-content/60">
            {!isBar && <span className="hidden sm:inline">Go to</span>}
            <input
                type="number"
                min={1}
                max={totalPages}
                value={jumpValue}
                placeholder={String(page + 1)}
                onChange={e => setJumpValue(e.target.value)}
                onKeyDown={handleJump}
                onBlur={handleJump}
                className={`input input-bordered rounded-lg text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    isBar ? 'input-xs w-10 h-[30px]' : 'input-sm w-14'
                }`}
                aria-label="Jump to page"
                inputMode="numeric"
            />
            <span className="shrink-0">/ {totalPages}</span>
        </div>
    );

    const pageButtons = pages.map((p, i) =>
        p === 'ellipsis' ? (
            <span key={`e${i}`}
                  className={`flex items-center justify-center text-base-content/40 ${isBar ? 'w-[30px] h-[30px] text-xs' : 'w-[34px] h-[34px] text-sm'}`}>
                …
            </span>
        ) : (
            <button
                key={p}
                onClick={() => goToPage(p)}
                disabled={loading}
                aria-current={p === page ? 'page' : undefined}
                className={`btn rounded-lg font-medium border transition-colors ${isBar ? 'btn-xs w-[30px] h-[30px] p-0 text-xs' : 'btn-sm w-[34px] h-[34px] p-0 text-sm'} ${
                    p === page
                        ? 'bg-primary border-primary text-white hover:bg-primary hover:border-primary'
                        : 'btn-outline border-base-200 hover:border-primary hover:bg-primary/10 hover:text-primary'
                }`}
            >
                {p + 1}
            </button>
        )
    );

    if (isBar) {
        return (
            <div className="flex items-center justify-between sm:justify-end gap-2 w-full">
                <button
                    className="btn btn-sm rounded-lg w-[30px] h-[30px] p-0 shrink-0 btn-outline border-base-200 hover:border-primary hover:bg-primary/10 hover:text-primary"
                    disabled={isFirst || loading}
                    onClick={() => goToPage(page - 1)}
                    aria-label="Previous page"
                >←</button>

                {/* Mobile: count only */}
                <span className="text-[10px] font-black pt-dark-blue uppercase tracking-widest whitespace-nowrap sm:hidden">
                    {from}–{to} of {totalElements}
                </span>

                {/* Desktop: page buttons + jump */}
                <div className="hidden sm:flex items-center gap-1">
                    {pageButtons}
                    <div className="w-px h-4 bg-base-200 mx-1"/>
                    {jumpInput}
                </div>

                <button
                    className="btn btn-sm rounded-lg w-[30px] h-[30px] p-0 shrink-0 btn-outline border-base-200 hover:border-primary hover:bg-primary/10 hover:text-primary"
                    disabled={isLast || loading}
                    onClick={() => goToPage(page + 1)}
                    aria-label="Next page"
                >→</button>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center gap-3 w-full">
            <div className="flex items-center gap-1 w-full justify-center">
                <button
                    className="btn btn-sm rounded-lg w-[34px] h-[34px] p-0 shrink-0 btn-outline border-base-200 hover:border-primary hover:bg-primary/10 hover:text-primary"
                    disabled={isFirst || loading}
                    onClick={() => goToPage(page - 1)}
                    aria-label="Previous page"
                >←</button>

                <div className="flex flex-wrap items-center justify-center gap-1 mx-1">
                    {pageButtons}
                </div>

                <button
                    className="btn btn-sm rounded-lg w-[34px] h-[34px] p-0 shrink-0 btn-outline border-base-200 hover:border-primary hover:bg-primary/10 hover:text-primary"
                    disabled={isLast || loading}
                    onClick={() => goToPage(page + 1)}
                    aria-label="Next page"
                >→</button>
            </div>

            <div className="flex items-center gap-3">
                {count}
                <div className="w-px h-4 bg-base-200"/>
                {jumpInput}
            </div>
        </div>
    );
}